import { useState } from "react";
import { POLICY_HINT, checkPassword } from "../utils/passwordPolicy";

interface Props {
  title: string;
  subjectLabel: string;
  description?: string;
  /** @deprecated the policy is central now; kept so callers still compile. */
  /** Extra wording under the field. There is deliberately NO
   * "customMinLength": three call sites used to pass 10, the shared policy
   * requires 12, and the server is the control — so honouring it would have
   * promised a length the save was always going to refuse. */
  customHint?: string;
  submitLabel?: string;
  onClose: () => void;
  /**
   * The caller decides what API to hit based on ``mode``. On success, return
   * the resulting password (so the modal can show it via PasswordRevealModal),
   * or return ``null`` to suppress the reveal (already handled out of band).
   * Throw to surface an error inline.
   */
  onSubmit: (mode: "auto" | "custom", customPassword?: string) => Promise<string | null>;
}

export default function PasswordResetModal({
  title,
  subjectLabel,
  description,
  customHint,
  submitLabel = "Reset",
  onClose,
  onSubmit,
}: Props) {
  const [mode, setMode] = useState<"auto" | "custom">("auto");
  const [customPw, setCustomPw] = useState("");
  const [customConfirm, setCustomConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [reveal, setReveal] = useState<string | null>(null);

  const submit = async () => {
    setError("");
    if (mode === "custom") {
      const pw = customPw.trim();
      // One policy, checked the same way the server will check it. This used
      // to be a bare length test that let "password12" through the browser
      // and then failed on the server with a different message.
      const problem = checkPassword(pw, { username: subjectLabel });
      if (problem) {
        setError(problem);
        return;
      }
      if (pw !== customConfirm.trim()) {
        setError("Passwords don't match");
        return;
      }
    }
    setBusy(true);
    try {
      const result = await onSubmit(mode, mode === "custom" ? customPw.trim() : undefined);
      if (result) {
        setReveal(result);
      } else {
        onClose();
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 dark:text-gray-100 rounded-xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        {reveal ? (
          <>
            <h2 className="text-lg font-semibold mb-2 dark:text-gray-100">Password set</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              New password for <strong>{subjectLabel}</strong>. Shown once — copy and hand it over
              securely.
            </p>
            <div className="bg-gray-900 text-green-300 font-mono text-base rounded-lg px-3 py-3 select-all break-all mb-3">
              {reveal}
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => navigator.clipboard?.writeText(reveal)}
                className="border dark:border-gray-600 dark:text-gray-100 text-sm px-4 py-2 rounded-lg"
              >
                Copy
              </button>
              <button
                onClick={onClose}
                className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg"
              >
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-lg font-semibold mb-2 dark:text-gray-100">{title}</h2>
            {description && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{description}</p>
            )}
            <div className="space-y-2 mb-4">
              <label
                className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded-lg border hover:bg-gray-50 dark:hover:bg-gray-700/40"
                style={{ borderColor: mode === "auto" ? "#2563eb" : "#e5e7eb" }}
              >
                <input
                  type="radio"
                  name="pw-reset-mode"
                  value="auto"
                  checked={mode === "auto"}
                  onChange={() => setMode("auto")}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium dark:text-gray-100">Auto-generate a strong password</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    20 characters, mixed case + digits + symbols. Shown once.
                  </div>
                </div>
              </label>
              <label
                className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded-lg border hover:bg-gray-50 dark:hover:bg-gray-700/40"
                style={{ borderColor: mode === "custom" ? "#2563eb" : "#e5e7eb" }}
              >
                <input
                  type="radio"
                  name="pw-reset-mode"
                  value="custom"
                  checked={mode === "custom"}
                  onChange={() => setMode("custom")}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="font-medium dark:text-gray-100">Set a custom password</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    {customHint || POLICY_HINT}
                  </div>
                  {mode === "custom" && (
                    <div className="space-y-2">
                      <input
                        type="text"
                        autoComplete="new-password"
                        value={customPw}
                        onChange={(e) => setCustomPw(e.target.value)}
                        placeholder="New password"
                        className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm font-mono"
                      />
                      <input
                        type="text"
                        autoComplete="new-password"
                        value={customConfirm}
                        onChange={(e) => setCustomConfirm(e.target.value)}
                        placeholder="Confirm password"
                        className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm font-mono"
                      />
                    </div>
                  )}
                </div>
              </label>
            </div>
            {error && (
              <p className="text-sm text-red-500 dark:text-red-300 bg-red-50 dark:bg-red-900/40 px-3 py-2 rounded mb-3">
                {error}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                disabled={busy}
                className="border dark:border-gray-600 dark:text-gray-100 text-sm px-4 py-2 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={busy}
                className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50"
              >
                {busy ? "Working…" : submitLabel}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
